<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col cols="10">
					<v-toolbar-title>{{ $t('add-diagnosis-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 200px)">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-select
						:label="$t('type')"
						v-model="selectedType"
						:items="diagnosisTypeTemplate"
						:item-text="'type'"
						outlined
						hide-details
						dense
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ item.type }}</span>
								</template>
								{{ $t(item.description) }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-autocomplete
						@input="handleAutoCompleteInput"
						v-model="selectedVerifier"
						:items="getICD10Codes"
						:item-text="$i18n.locale === 'en' ? 'shortDescription' : 'shortDescriptionEsp'"
						item-value="lowValue"
						hide-details
						outlined
						dense
						:label="$t('main-category-of-diagnosis')"
						:menu-props="{ maxWidth: '450' }" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-text-field
						outlined
						dense
						v-model="search"
						append-icon="mdi-magnify"
						:label="$t('search_diagnose')"
						hide-details>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="12">
					<v-radio-group v-model="selectedDiagnosis" dense hide-details class="my-0 pa-0">
						<v-data-table
							class="ma-0 pa-0 data-table-outline border-radius0"
							dense
							:loading="loading"
							:height="Math.max(parseInt(pageHeight(-90)), 260)"
							:headers="headers"
							fixed-header
							:no-data-text="$t('no-data-available')"
							:items="filteredCategory"
							:items-per-page="-1"
							:footer-props="{ 'items-per-page-text': '' }">
							<template v-slot:item="{ item }">
								<tr class="cursor" @click="selectedDiagnosis = item">
									<td class="text-left font-size12">{{ item.icd10Code }}</td>
									<td class="text-left font-size12">
										{{ item.fullDescription }}
									</td>
									<td class="text-left font-size12">
										<v-radio :value="item" />
									</td>
								</tr>
							</template>
						</v-data-table>
					</v-radio-group>
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpent"
						@update:inputValue="(val) => (timeSpent = val)"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip left v-if="!addButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('add') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="addDiagnosis()" color="dark_purple" style="color: white" block>
						{{ $t('add') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { getDataTableFooterProps, showErrorAlert } from '@/utils/utilities';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMDiagnosisEdit',
	data () {
		return {
			search: '',
			diagnose: '',
			category: [],
			selectedDiagnosis: null,
			selectedType: null,
			selectedVerifier: '',
			timeSpent: 0,
			loading: false,
			diagnosisTypeTemplate: null,
		};
	},
	computed: {
		...mapState({
			userData: (state) => state.authentication.userData,
			verifiersFromRepo: (state) => state.verifiers.verifiers,
			detailedUser: (state) => state.users.detailedUser,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
		}),
		...mapGetters({
			getICD10Codes: 'verifiers/getICD10Codes',
			pageHeight: 'pageHeight',
		}),
		addButtonEnabled () {
			return this.selectedDiagnosis && this.selectedType && this.timeSpent && this.timeSpent <= 7200;
		},
		headers () {
			return [
				{
					text: 'ICD-10',
					sortable: false,
					value: 'ICD_10',
					class: 'color_header',
				},
				{
					text: this.$t('diagnose'),
					sortable: false,
					value: 'diagnose',
					class: 'color_header',
				},
				{ text: '', sortable: false, class: 'color_header' },
			];
		},
		filteredCategory () {
			if (!this.search) {
				return this.category;
			}
			const keyword = this.search.toLowerCase();
			return this.category.filter((item) => {
				return item.icd10Code.toLowerCase().includes(keyword) || item.fullDescription.toLowerCase().includes(keyword);
			});
		},
	},
	async mounted () {
		this.diagnose = this.detailedUser.userData.patientMetadata.diagnose;
		await this.getVerifiersForDomain();
		if (!this.diagnosisTypeTemplate) {
			const response = await this.getAllDiagnosisTypes();
			this.diagnosisTypeTemplate = response.data;
		}
	},
	methods: {
		...mapActions({
			getAllDiagnosisTypes: 'ccmRecords/getAllDiagnosisTypes',
			addCCMDiagnosis: 'ccmDiagnoses/addCCMDiagnosis',
			getDiagnosisByCategory: 'patientmetadata/getDiagnosesByCategory',
			getICD10CodeForSpecialization: 'verifiers/getICD10CodeForDomainSpecialization',
		}),
		getDataTableFooterProps,
		async addDiagnosis () {
			const body = {
				ccmRecordId: this.currentRecord.id,
				patientId: this.detailedUser.userData.id,
				staffId: this.userData.id,
				hospitalId: localStorage.getItem('selectedHospitalAssignedId'),
				diagnosisType: this.selectedType,
				diagnosisCode: this.selectedDiagnosis.icd10Code,
				diagnoseDescription: this.selectedDiagnosis.fullDescription,
				timeSpentSeconds: parseInt(this.timeSpent),
				diagnoseTimestampUTC: new Date().toISOString(),
				additionalData: '',
			};
			const response = await this.addCCMDiagnosis(body);
			if (!response.resFlag) {
				showErrorAlert(this.$t('icdCodeError'));
			}

			this.closeDrawer();
		},
		closeDrawer () {
			this.selectedVerifier = '';
			this.search = '';
			this.category = [];
			this.selectedDiagnosis = null;
			this.selectedType = null;
			this.selectedVerifier = '';
			this.timeSpent = 0;
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		async handleAutoCompleteInput () {
			this.loading = true;
			if (!this.selectedVerifier) {
				return;
			}
			try {
				const body = this.selectedVerifier.toString();
				const response = await this.getDiagnosisByCategory(body);
				if (response && response.data) {
					this.category = response?.data;
					this.loading = false;
				}
			} catch (error) {
				// test
			}
		},
		async getVerifiersForDomain () {
			await this.getICD10CodeForSpecialization();
		},
	},
	watch: {
		searchDiagnose (e) {
			const itemsCopy = this.diagnose;
			if (e === '') {
				this.diagnose = this.verifiersFromRepo;
				return;
			}
			this.diagnose = itemsCopy.filter((item) => {
				return item.diagnose.toLowerCase().indexOf(e.toLowerCase()) > -1;
			});
		},
	},
	components: { InputTimeFormatter },
};
</script>
